import { format, min } from 'date-fns'
import { orderSelectors } from 'ducks/orderComponents'
import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { evaluateCart, isDealActive, isFreeShippingDeal } from '../dealEngine'
import { CartLineInput, Deal, DealAllocation, DealBreakdownEntry, ItemDealResult } from '../types'
import { useCartDeals } from './useCartDeals'

/** Ordering row fields read by buildCartInputs (subset of ordering_v2 / distributor entry). */
export type DealTotalsOrderingEntry = {
  distributor: string
  component_id?: number
  price?: number
  price_detail?: {
    price_ex_vat: number
    discount_value?: number
    minimum_quantity?: number
  }
  deals?: Deal[]
}

/** Minimal cart line shape consumed by buildCartInputs / useDealTotals */
export type DealTotalsLineItem = {
  code: string
  selectedDistributor?: string
  quantity: number
  componentType?: string
  orderingData?: DealTotalsOrderingEntry[]
}

export interface DealTotalsResult {
  discountTotal: number
  freeShipping: string[]
  subtotal: number
  dealBreakdown: DealBreakdownEntry[]
  /** Per-item discount map keyed by "code:distributor" — used by CartTable rows */
  itemDiscounts: Map<string, ItemDealResult>
  /** Per-deal per-line attributions for checkout webhook snapshot */
  allocations: DealAllocation[]
  earliestExpiryDate: string | null
}

/**
 * Transforms Redux line items (or external line items) into the shapes
 * expected by evaluateCart: CartLineInput[] for cart items, and a deduped
 * DealLike[] for all product-level deals attached to those items.
 */
export function buildCartInputs(lineItems: DealTotalsLineItem[]): {
  items: CartLineInput[]
  productDeals: Deal[]
  subtotal: number
} {
  const items: CartLineInput[] = []
  const dealMap = new Map<number, Deal>()
  let subtotal = 0

  for (const item of lineItems) {
    const dist = item.selectedDistributor
    if (!dist || dist === 'component') continue

    const orderingEntries = item.orderingData || []
    const distEntry = orderingEntries.find((e: DealTotalsOrderingEntry) => e.distributor === dist)
    if (!distEntry) continue

    const unitPrice: number = distEntry.price ?? distEntry.price_detail?.price_ex_vat ?? 0
    if (!unitPrice) continue

    const componentId: number = distEntry.component_id ?? 0

    items.push({
      componentId,
      code: item.code,
      quantity: item.quantity,
      price: unitPrice,
      distributor: dist,
      category: item.componentType || '',
    })

    subtotal += unitPrice * item.quantity

    const deals: Deal[] = distEntry.deals || []
    for (const deal of deals) {
      if (!dealMap.has(deal.id)) {
        dealMap.set(deal.id, deal)
      }
    }
  }

  return { items, productDeals: Array.from(dealMap.values()), subtotal }
}

const getEarliestExpiryDate = (deals: Deal[]): string | null => {
  const expiryDates = deals.filter((d) => isDealActive(d) && d.expires_at).map((d) => new Date(d.expires_at!))
  if (!expiryDates.length) return null
  return format(min(expiryDates), 'dd MMMM yyyy')
}

/** Deals that evaluateCart applied: discount breakdown entries plus free-shipping deals. */
const getAppliedDealsForExpiry = (
  allDeals: Deal[],
  breakdown: DealBreakdownEntry[],
  freeShippingDistributors: string[]
): Deal[] => {
  const appliedDealIds = new Set(breakdown.map((entry) => entry.dealId))
  const freeShippingSet = new Set(freeShippingDistributors)

  return allDeals.filter(
    (deal) =>
      appliedDealIds.has(deal.id) ||
      (isFreeShippingDeal(deal) && Boolean(deal.distributor) && freeShippingSet.has(deal.distributor))
  )
}

/**
 * Computes deal totals using the cart-level evaluateCart engine.
 * Handles all condition types (cart_subtotal, cart_quantity, item_quantity),
 * all apply_to modes (each_item, cart_total, bundle_total), compound stacking,
 * bundles, BXGY, and free shipping in a single pass.
 *
 * @param externalLineItems - When provided, uses these instead of Redux cart state.
 */
export const useDealTotals = (externalLineItems?: DealTotalsLineItem[]): DealTotalsResult => {
  const cartLineItems = useSelector(orderSelectors.getOrderLineItems)
  const lineItems = externalLineItems ?? cartLineItems
  const { cartDeals } = useCartDeals()

  return useMemo(() => {
    const emptyDiscounts = new Map<string, ItemDealResult>()
    const empty: DealTotalsResult = {
      discountTotal: 0,
      freeShipping: [],
      subtotal: 0,
      dealBreakdown: [],
      itemDiscounts: emptyDiscounts,
      allocations: [],
      earliestExpiryDate: null,
    }
    if (!lineItems.length) return empty

    const { items, productDeals, subtotal } = buildCartInputs(lineItems)
    if (!items.length) return { ...empty, subtotal }

    // Merge product-level deals (from ordering_v2) with cart-level deals (from API)
    const seenIds = new Set(productDeals.map((d) => d.id))
    const allDeals = [...productDeals]
    for (const cd of cartDeals) {
      if (!seenIds.has(cd.id)) {
        allDeals.push(cd)
        seenIds.add(cd.id)
      }
    }

    if (!allDeals.length) return { ...empty, subtotal }

    const result = evaluateCart(items, allDeals)

    const dealBreakdown: DealBreakdownEntry[] = result.breakdown.map((e) => ({
      dealId: e.dealId,
      title: e.title,
      discount: e.discount,
      supplier: e.supplier,
    }))

    const discountTotal = Math.round(Math.min(result.discountTotal, subtotal) * 100) / 100

    const appliedDeals = getAppliedDealsForExpiry(allDeals, result.breakdown, result.freeShipping)
    const earliestExpiryDate = getEarliestExpiryDate(appliedDeals)

    return {
      discountTotal,
      freeShipping: result.freeShipping,
      subtotal,
      dealBreakdown,
      itemDiscounts: result.itemDiscounts,
      allocations: result.allocations,
      earliestExpiryDate,
    }
  }, [lineItems, cartDeals])
}
